// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  type FetchBaseQueryError,
  type FetchBaseQueryMeta,
  type QueryReturnValue,
} from '@reduxjs/toolkit/query';
import {
  auth0Api,
  Auth0TagTypes,
} from '@shared/data-access/api-base/citec-auth0-api';
import { selectToken } from '@shared/data-access/slices/auth/auth-selectors';
import { type RootAuthState } from '@shared/data-access/slices/auth/auth-state';
import {
  type Auth0TokenResponse,
  type Auth0ExchangeCodeParams,
  type Auth0RefreshTokenParams,
  type Auth0RevokeTokenParams,
  type Auth0UserinfoResponse,
} from '@shared/data-access/types/auth0-types';
import { environment, type BrandDomain } from '@shared/environment';
import { PKCE_VERIFIER_KEY, generatePKCE } from '@shared/data-access/utils';

/**
 * Auth0 API – Authentication (OAuth2 / OIDC).
 *
 * This API layer talks to the **Auth0 Authentication API** for token exchange,
 * refresh, user profile, and authorization URL generation. Used for login, sign-up,
 * and session management.
 *
 * @remarks
 * - **Token endpoint** (`POST /oauth/token`): exchange authorization code for tokens,
 *   or refresh access token using refresh_token grant.
 * - **Revoke** (`POST /oauth/revoke`): revoke a token (e.g. refresh_token) before
 *   logout.
 * - **Userinfo** (`GET /userinfo`): fetch profile for the current access token.
 * - **Authorize URL**: build PKCE authorize URL for redirecting the user to Auth0
 *   Universal Login (login or signup via `screen_hint`).
 *
 * All token/revoke requests use `application/x-www-form-urlencoded`. PKCE (RFC
 * 7636) is used for the authorization code flow.
 * @see {@link https://auth0.com/docs/api/authentication} Auth0 Authentication API.
 */
export const authApi = auth0Api.injectEndpoints({
  endpoints: build => ({
    /**
     * Exchange authorization code for tokens.
     *
     * Completes the OAuth2 authorization code flow after the user is redirected
     * back from Auth0 with a `code`. Sends the code plus PKCE `code_verifier`
     * (stored in sessionStorage during the redirect to Auth0) to obtain
     * `access_token` and optionally `refresh_token`.
     *
     * @remarks
     * Call this from the OAuth callback route. The code verifier must have been
     * stored when generating the authorize URL (e.g. via `getLoginUrl`).
     * @example
     *
     * ```tsx
     * const [exchangeCode] = authApi.useExchangeCodeMutation();
     * const result = await exchangeCode({
     *   grant_type: 'authorization_code',
     *   client_id: env.auth.clientId,
     *   code: searchParams.get('code'),
     *   redirect_uri: callbackUrl,
     *   code_verifier: sessionStorage.getItem(PKCE_VERIFIER_KEY),
     * }).unwrap();
     * // result.access_token, result.refresh_token, result.expires_in
     * ```
     *
     * @param params - Request body for `POST /oauth/token` (grant_type, client_id,
     *   code, redirect_uri, code_verifier).
     * @returns {@link Auth0TokenResponse} With access_token, expires_in, etc.
     * @see {@link https://auth0.com/docs/api/authentication#authorization-code-flow}
     */
    exchangeCode: build.mutation<Auth0TokenResponse, Auth0ExchangeCodeParams>({
      query: params => ({
        url: '/oauth/token',
        method: 'POST',
        body: new URLSearchParams(
          params as unknown as Record<string, string>,
        ).toString(),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      }),
    }),

    /**
     * Refresh access token using refresh token.
     *
     * Sends a refresh_token grant to `POST /oauth/token` to obtain a new
     * access_token and optionally a new refresh_token. Used by the base query layer
     * when a request returns 401 and a refresh token is available.
     *
     * @remarks
     * Auth0 may return errors such as `invalid_grant` or `expired_token` when the
     * refresh token is invalid or revoked; the caller should handle these and
     * redirect to login if needed.
     * @example
     *
     * ```tsx
     * const [refreshTokens] = authApi.useRefreshTokensMutation();
     * const tokens = await refreshTokens({
     *   grant_type: 'refresh_token',
     *   client_id: env.auth.clientId,
     *   refresh_token: currentRefreshToken,
     * }).unwrap();
     * ```
     *
     * @param params - Request body (grant_type, client_id, refresh_token).
     * @returns {@link Auth0TokenResponse} With new access_token and expires_in.
     * @see {@link https://auth0.com/docs/api/authentication#refresh-token}
     */
    refreshTokens: build.mutation<Auth0TokenResponse, Auth0RefreshTokenParams>({
      query: params => ({
        url: '/oauth/token',
        method: 'POST',
        body: new URLSearchParams(
          params as unknown as Record<string, string>,
        ).toString(),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      }),
      invalidatesTags: [Auth0TagTypes.Auth0Userinfo],
    }),

    /**
     * Revoke a token.
     *
     * Calls Auth0 `POST /oauth/revoke` to revoke the given token (e.g.
     * refresh_token) so it can no longer be used. Typically called before
     * redirecting the user to Auth0 `/v2/logout` for a full logout.
     *
     * @remarks
     * Optional `token_type_hint` can be `refresh_token` or `access_token` to help
     * Auth0 identify the token type.
     * @example
     *
     * ```tsx
     * const [revokeToken] = authApi.useRevokeTokenMutation();
     * await revokeToken({
     *   client_id: env.auth.clientId,
     *   token: refreshToken,
     *   token_type_hint: 'refresh_token',
     * });
     * ```
     *
     * @param params - Client_id, token, and optional token_type_hint.
     * @returns Void on success.
     * @see {@link https://auth0.com/docs/api/authentication#revoke-token}
     */
    revokeToken: build.mutation<void, Auth0RevokeTokenParams>({
      query: params => {
        const body: Record<string, string> = {
          client_id: params.client_id,
          token: params.token,
        };
        if (params.token_type_hint) {
          body['token_type_hint'] = params.token_type_hint;
        }

        return {
          url: '/oauth/revoke',
          method: 'POST',
          body: new URLSearchParams(body).toString(),
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        };
      },
    }),

    /**
     * Get user profile (userinfo).
     *
     * Fetches the profile for the current access token from Auth0 `GET /userinfo`.
     * The token is read from the auth slice and sent as the `access_token` query
     * parameter (Auth0 supports this format).
     *
     * @remarks
     * Implemented as a query with custom `queryFn` so the token is taken from the
     * store at request time. When there is no token, the request is skipped (e.g.
     * via `skipToken` where used).
     * @example
     *
     * ```tsx
     * const { data: user } = authApi.useGetUserInfoQuery(
     *   token ? undefined : skipToken,
     * );
     * ```
     *
     * @returns {@link Auth0UserinfoResponse} (user profile claims).
     * @see {@link https://auth0.com/docs/api/authentication#get-user-info}
     */
    getUserInfo: build.query<Auth0UserinfoResponse, void>({
      queryFn: async (_arg, api, _extraOptions, fetchWithBQ) => {
        const token = selectToken(api.getState() as RootAuthState);
        const url = token ? `/userinfo?access_token=${token}` : '/userinfo';
        return fetchWithBQ({
          url,
          headers: { Accept: 'application/json' },
        }) as Promise<
          QueryReturnValue<
            Auth0UserinfoResponse,
            FetchBaseQueryError,
            FetchBaseQueryMeta
          >
        >;
      },
      providesTags: [Auth0TagTypes.Auth0Userinfo],
    }),

    /**
     * Get Auth0 Universal Login authorize URL.
     *
     * Builds the URL to redirect the user to Auth0 for login or sign-up. Uses PKCE
     * (RFC 7636): generates code_verifier and code_challenge, stores code_verifier
     * in sessionStorage for the callback, and adds code_challenge to the authorize
     * URL. Implemented as a mutation so each call produces a new PKCE pair (no
     * cache).
     *
     * @remarks
     * Optional `screenHint` is sent as the `screen_hint` query parameter. Use
     * `'login'` (default) or `'signup'` to open the login or sign-up screen in
     * Auth0 Universal Login.
     * @example
     *
     * ```tsx
     * const [getLoginUrl] = authApi.useGetLoginUrlMutation();
     * const url = await getLoginUrl({
     *   redirectUri: `${origin}/callback`,
     *   state: randomState(),
     *   screenHint: 'signup',
     * }).unwrap();
     * window.location.href = url;
     * ```
     *
     * @param params - RedirectUri, state, and optional screenHint (default
     *   'login').
     * @returns The full authorize URL string (redirect the user to this URL).
     * @see {@link https://auth0.com/docs/authenticate/login/auth0-universal-login/new-experience#screen-hint}
     * @see {@link https://auth0.com/docs/get-started/authentication-authorization-flow/authorization-code-flow-with-pkce}
     */
    getLoginUrl: build.mutation<
      string,
      {
        redirectUri: string;
        state: string;
        screenHint?: string;
        locale?: string;
        domain?: BrandDomain;
      }
    >({
      queryFn: async ({
        redirectUri,
        state,
        screenHint = 'login',
        locale = 'en',
        domain = 'citec',
      }) => {
        const { codeVerifier, codeChallenge } = await generatePKCE();

        sessionStorage.setItem(PKCE_VERIFIER_KEY, codeVerifier);

        const authConfig = environment.AUTH;

        const base = `https://${authConfig.DOMAIN!}`;

        const params = new URLSearchParams({
          response_type: 'code',
          client_id: authConfig.CLIENT_ID!,
          redirect_uri: redirectUri,
          scope: authConfig.SCOPE,
          audience: authConfig.AUDIENCE!,
          state,
          code_challenge: codeChallenge,
          code_challenge_method: 'S256',
        });

        params.set('screen_hint', screenHint);
        params.set('ui_locales', locale);
        params.set('domain', domain);

        return { data: `${base}/authorize?${params.toString()}` };
      },
    }),

    /**
     * Get Auth0 logout URL.
     *
     * Builds the URL to redirect the user to Auth0 `/v2/logout` so Auth0 clears its
     * session and cookies. After logout, Auth0 redirects the user to `returnTo`
     * (must be in Allowed Logout URLs in the Auth0 application).
     *
     * @remarks
     * Call this after clearing local app state (auth slice, storage, onLogout).
     * Then assign `window.location.href = url` to perform the redirect.
     * @param params - ReturnTo: URL to redirect after logout (e.g. origin).
     * @returns The full logout URL string.
     * @see {@link https://auth0.com/docs/logout}
     */
    getLogoutUrl: build.mutation<string, { returnTo: string }>({
      queryFn: ({ returnTo }) => {
        const authConfig = environment.AUTH;

        const base = `https://${authConfig.DOMAIN!}`;
        const params = new URLSearchParams({
          client_id: authConfig.CLIENT_ID!,
          returnTo,
        });
        return { data: `${base}/v2/logout?${params.toString()}` };
      },
    }),
  }),
});
